import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, query, where, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { softDelete } from '../services/firestoreService';
import '../styles/DocumentManager.css';

const DocumentManager = ({ candidateId }) => {
  const { currentUser } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    category: 'transcript',
    description: '',
    file: null
  });

  const documentCategories = [
    { value: 'transcript', label: 'Transcript' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'recommendation', label: 'Letter of Recommendation' },
    { value: 'application', label: 'Application Form' },
    { value: 'background-check', label: 'Background Check' },
    { value: 'medical', label: 'Medical Records' },
    { value: 'psychological', label: 'Psychological Evaluation' },
    { value: 'interview', label: 'Interview Notes' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (candidateId) {
      fetchDocuments();
    }
  }, [candidateId]);

  const fetchDocuments = async () => {
    try {
      const documentsRef = collection(db, 'documents');
      const q = query(documentsRef, where('candidateId', '==', candidateId));
      const snapshot = await getDocs(q);
      
      const documentsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDocuments(documentsList);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUploadData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setUploadData(prev => ({
      ...prev,
      file: file
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file) {
      toast.warning('Please select a file to upload.');
      return;
    }

    setUploading(true);

    try {
      // Create a unique filename
      const timestamp = Date.now();
      const filename = `${candidateId}/${timestamp}_${uploadData.file.name}`;
      const storageRef = ref(storage, `documents/${filename}`);

      // Upload file to Firebase Storage
      const snapshot = await uploadBytes(storageRef, uploadData.file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Save document metadata to Firestore
      const documentData = {
        candidateId,
        title: uploadData.title,
        category: uploadData.category,
        description: uploadData.description,
        filename: uploadData.file.name,
        fileSize: uploadData.file.size,
        fileType: uploadData.file.type,
        downloadURL,
        storagePath: filename,
        uploadedAt: serverTimestamp(),
        uploadedBy: currentUser?.uid || 'unknown',
        deleted: false,
      };

      await addDoc(collection(db, 'documents'), documentData);
      
      fetchDocuments();
      resetUploadForm();
      toast.success('Document uploaded successfully!');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Error uploading document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (document) => {
    const confirmed = await confirm({
      title: 'Delete Document',
      message: `Are you sure you want to delete "${document.title}"?`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        // Soft-delete the Firestore record (preserves for audit/recovery)
        await softDelete('documents', document.id, currentUser);

        // Also remove the file from Storage
        try {
          const storageRef = ref(storage, `documents/${document.storagePath}`);
          await deleteObject(storageRef);
        } catch {
          // Storage file may already be gone — not fatal
        }

        fetchDocuments();
        toast.success('Document deleted.');
      } catch (error) {
        console.error('Error deleting document:', error);
        toast.error('Error deleting document. Please try again.');
      }
    }
  };

  const resetUploadForm = () => {
    setUploadData({
      title: '',
      category: 'transcript',
      description: '',
      file: null
    });
    setShowUploadModal(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCategoryLabel = (category) => {
    const cat = documentCategories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const groupDocumentsByCategory = () => {
    const grouped = {};
    
    documents.forEach(doc => {
      const category = doc.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(doc);
    });
    
    // Sort documents within each category by upload date (newest first)
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => {
        const dateA = a.uploadedAt?.toDate?.() || new Date(0);
        const dateB = b.uploadedAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });
    });
    
    return grouped;
  };

  if (loading) {
    return (
      <div className="document-manager">
        <div className="loading-spinner">Loading documents...</div>
      </div>
    );
  }

  const groupedDocuments = groupDocumentsByCategory();

  return (
    <div className="document-manager">
      <div className="documents-header">
        <h3>Documents & Attachments</h3>
        <button 
          onClick={() => setShowUploadModal(true)}
          className="upload-btn"
        >
          Upload Document
        </button>
      </div>

      <div className="documents-list">
        {documents.length === 0 ? (
          <div className="no-documents">
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem', opacity: 0.4 }}>📄</div>
            <h4 style={{ margin: '0 0 0.5rem', color: '#2c3e50' }}>No documents on file</h4>
            <p style={{ margin: '0 0 1.25rem', color: '#6c757d', fontSize: '0.875rem' }}>
              Upload transcripts, background checks, letters of recommendation, and other candidate materials here.
            </p>
            <button onClick={() => setShowUploadModal(true)} className="upload-btn">
              Upload First Document
            </button>
          </div>
        ) : (
          documentCategories.map(category => {
            const categoryDocs = groupedDocuments[category.value];
            if (!categoryDocs || categoryDocs.length === 0) return null;
            
            return (
              <div key={category.value} className="document-category-section">
                <h4 className="category-heading">
                  {category.label} ({categoryDocs.length})
                </h4>
                <div className="category-documents">
                  {categoryDocs.map(document => (
                    <div key={document.id} className="document-item">
                      <div className="document-info">
                        <div className="document-header">
                          <h5>{document.title}</h5>
                        </div>
                        
                        <div className="document-details">
                          <p><strong>Filename:</strong> {document.filename}</p>
                          <p><strong>Size:</strong> {formatFileSize(document.fileSize)}</p>
                          <p><strong>Uploaded:</strong> {document.uploadedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}</p>
                          {document.description && (
                            <p><strong>Description:</strong> {document.description}</p>
                          )}
                        </div>
                      </div>

                      <div className="document-actions">
                        <a 
                          href={document.downloadURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="download-btn"
                        >
                          Download
                        </a>
                        <button 
                          onClick={() => handleDelete(document)}
                          className="delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Upload Document</h2>
              <button 
                onClick={resetUploadForm}
                className="close-btn"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpload} className="upload-form">
              <div className="form-group">
                <label htmlFor="title">Document Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={uploadData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={uploadData.category}
                  onChange={handleInputChange}
                  required
                >
                  {documentCategories.map(category => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={uploadData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="file">Select File *</label>
                <input
                  type="file"
                  id="file"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  required
                />
                <small>Supported formats: PDF, DOC, DOCX, JPG, PNG, TXT (Max 10MB)</small>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={resetUploadForm}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={uploading}
                  className="upload-submit-btn"
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManager;
